import editorStyles from "~/scss/editor.css?inline";
import { entry } from "~/../colors.json";

const { convertToGermanQuotationMarks } = useTinymce();

export const editorInit = (onInit: (editor: any) => void) => {
  const { locale, t } = useI18n();

  const getStyleFormats = () => [
    {
      title: t("legal_text.textEditor.tooltips.summary"),
      format: "summary",
    },
    {
      title: t("legal_text.textEditor.tooltips.reminder"),
      format: "reminder",
    },
    {
      title: t("legal_text.textEditor.tooltips.definition"),
      format: "definition",
    },
    {
      title: t("legal_text.textEditor.tooltips.problem"),
      format: "problem",
    },
    {
      title: t("legal_text.textEditor.tooltips.structure_note"),
      format: "structure_note",
    },
  ];
  const blockFormats = [
    { title: t("legal_text.textEditor.blockFormats.standard"), format: "p" },
    { title: t("legal_text.textEditor.blockFormats.heading1"), format: "h1" },
    { title: t("legal_text.textEditor.blockFormats.heading2"), format: "h2" },
    { title: t("legal_text.textEditor.blockFormats.heading3"), format: "h3" },
    { title: t("legal_text.textEditor.blockFormats.heading4"), format: "h4" },
    { title: t("legal_text.textEditor.blockFormats.heading5"), format: "h5" },
    { title: t("legal_text.textEditor.blockFormats.heading6"), format: "h6" },
    { title: t("legal_text.textEditor.blockFormats.heading7"), format: "h7" },
    { title: t("legal_text.textEditor.blockFormats.heading8"), format: "h8" },
    { title: t("legal_text.textEditor.blockFormats.heading9"), format: "h9" },
    { title: t("legal_text.textEditor.blockFormats.heading10"), format: "h10" },
  ];

  const colorMap = [
    entry.yellow.lighter,
    t("legal_text.textEditor.colors.yellow"),
    entry.orange.lighter,
    t("legal_text.textEditor.colors.orange"),
    entry.red.lighter,
    t("legal_text.textEditor.colors.red"),
    entry.purple.lighter,
    t("legal_text.textEditor.colors.purple"),
    entry.blue.lighter,
    t("legal_text.textEditor.colors.blue"),
    entry.green.lighter,
    t("legal_text.textEditor.colors.green"),

    entry.yellow.lightest,
    t("legal_text.textEditor.colors.light_yellow"),
    entry.orange.lightest,
    t("legal_text.textEditor.colors.light_orange"),
    entry.red.lightest,
    t("legal_text.textEditor.colors.light_red"),
    entry.purple.lightest,
    t("legal_text.textEditor.colors.light_purple"),
    entry.blue.lightest,
    t("legal_text.textEditor.colors.light_blue"),
    entry.green.lightest,
    t("legal_text.textEditor.colors.light_green"),
  ];

  const blockFormatsString = blockFormats
    .map(({ title, format }) => `${title}=${format}`)
    .join("; ");

  return {
    init_instance_callback(editor) {
      editor.fire("focus");
    },
    setup(editor) {
      const scrollToSelectedNode = () =>
        editor.selection.getNode().scrollIntoView({ behavior: "smooth" });
      convertToGermanQuotationMarks(editor);
      editor.on("blur", function () {
        return false;
      });
      editor.on("ExecCommand", function (cmd) {
        if (cmd.command === "mceApplyTextcolor") {
          document.getSelection()?.collapseToStart();
        }
      });
      editor.on("keydown", function (event: KeyboardEvent) {
        setTimeout(() => {
          if (
            event.key === "Enter" &&
            window.tinymce.activeEditor.selection.getNode().nodeName !== "LI"
          ) {
            editor.formatter.apply("p");
          }
        }, 0);
      });
      editor.on("init", onInit);
      editor.on("Undo", scrollToSelectedNode);
      editor.on("Redo", scrollToSelectedNode);
    },
    external_plugins: {
      tableOfContents: "/editor_plugins/tableOfContents.js",
    },
    link_assume_external_targets: "https",
    convert_urls: false,
    branding: false,
    language: locale.value || "de", // de
    browser_spellcheck: true,
    height: "100%",
    menubar: false,
    color_map: colorMap,
    color_cols: 6,
    color_default_background: entry.yellow.lighter,
    statusbar: false,
    toolbar_mode: "floating",
    fixed_toolbar_container: "#editor_toolbar",
    content_style: editorStyles,
    paste_data_images: true,
    advlist_bullet_styles: "square,disc,circle",
    block_formats: blockFormatsString,
    style_formats: getStyleFormats(),
    formats: {
      summary: {
        block: "section",
        attributes: { "data-style": "summary" },
        wrapper: true,
        merge_siblings: false,
      },
      reminder: {
        block: "section",
        attributes: { "data-style": "reminder" },
        wrapper: true,
        merge_siblings: false,
      },
      definition: {
        block: "section",
        attributes: { "data-style": "definition" },
        wrapper: true,
        merge_siblings: false,
      },
      problem: {
        block: "section",
        attributes: { "data-style": "problem" },
        wrapper: true,
        merge_siblings: false,
      },
      structure_note: {
        block: "section",
        attributes: { "data-style": "structure_note" },
        wrapper: true,
        merge_siblings: false,
      },
      p: {
        block: "p",
        attributes: { "data-style": "standard" },
        remove: "none",
      },
      h1: {
        block: "h2",
        attributes: { "data-style": "heading1" },
        remove: "none",
      },
      h2: {
        block: "h3",
        attributes: { "data-style": "heading2" },
        remove: "none",
      },
      h3: {
        block: "h4",
        attributes: { "data-style": "heading3" },
        remove: "none",
      },
      h4: {
        block: "h5",
        attributes: { "data-style": "heading4" },
        remove: "none",
      },
      h5: {
        block: "h6",
        attributes: { "data-style": "heading5" },
        remove: "none",
      },
      h6: {
        block: "p",
        attributes: { "data-style": "heading6" },
        remove: "none",
      },
      h7: {
        block: "p",
        attributes: { "data-style": "heading7" },
        remove: "none",
      },
      h8: {
        block: "p",
        attributes: { "data-style": "heading8" },
        remove: "none",
      },
      h9: {
        block: "p",
        attributes: { "data-style": "heading9" },
        remove: "none",
      },
      h10: {
        block: "p",
        attributes: { "data-style": "heading10" },
        remove: "none",
      },
    },
    fontsize_formats_title: t("legal_text.textEditor.fontsize.title"),
    fontsize_formats: "6pt 8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt",
    end_container_on_empty_block: true,
    referrer_policy: "no-referrer",
    extended_valid_elements:
      "a[!href|target|width|height|title|rel=nofollow noreferrer]",
  };
};
